let mediaRecorderInstance: MediaRecorder | null = null;
let streamInstance: MediaStream | null = null;
let resolveRecording: ((blob: Blob) => void) | null = null;

/**
 * Start recording audio from the user's microphone.
 * @param maxSeconds - Stop automatically after this many seconds (0 = no auto stop).
 * @returns Object with stop() and blobPromise. Call stop() when user stops; blobPromise resolves with the recorded Blob.
 */
export function startRecording(maxSeconds: number = 30): { stop: () => void; blobPromise: Promise<Blob> } {
  const blobPromise = new Promise<Blob>((resolve, reject) => {
    resolveRecording = resolve;
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        streamInstance = stream;
        const mr = new MediaRecorder(stream);
        mediaRecorderInstance = mr;
        const chunks: Blob[] = [];

        mr.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };

        mr.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          streamInstance = null;
          mediaRecorderInstance = null;
          const fn = resolveRecording;
          resolveRecording = null;
          if (chunks.length > 0) {
            fn?.(new Blob(chunks, { type: mr.mimeType || 'audio/webm' }));
          } else {
            fn?.(new Blob([], { type: 'audio/webm' }));
          }
        };

        mr.onerror = () => {
          stream.getTracks().forEach((t) => t.stop());
          streamInstance = null;
          mediaRecorderInstance = null;
          resolveRecording = null;
          reject(new Error('Recording failed'));
        };

        mr.start();
        if (maxSeconds > 0) {
          setTimeout(() => {
            if (mediaRecorderInstance && mediaRecorderInstance.state !== 'inactive') {
              mediaRecorderInstance.stop();
            }
          }, maxSeconds * 1000);
        }
      })
      .catch((err) => {
        resolveRecording = null;
        reject(err);
      });
  });

  return {
    stop() {
      if (mediaRecorderInstance && mediaRecorderInstance.state !== 'inactive') {
        mediaRecorderInstance.stop();
      }
      if (streamInstance) {
        streamInstance.getTracks().forEach((t) => t.stop());
        streamInstance = null;
      }
      mediaRecorderInstance = null;
    },
    blobPromise,
  };
}

/**
 * Stop the current recording (if any). Prefer using the stop() returned from startRecording().
 */
export function stopRecording(): void {
  if (mediaRecorderInstance && mediaRecorderInstance.state !== 'inactive') {
    mediaRecorderInstance.stop();
  }
  if (streamInstance) {
    streamInstance.getTracks().forEach((t) => t.stop());
    streamInstance = null;
  }
  mediaRecorderInstance = null;
}

/**
 * Create an object URL for a Blob (e.g. for playback).
 * Caller should revoke the URL when done: URL.revokeObjectURL(url).
 */
export function blobToUrl(blob: Blob): string {
  return URL.createObjectURL(blob);
}
